import { useEffect, useState } from 'react'

export interface DebouncedValue<T> {
  debounced: T
  /**
   * Adopt a value immediately, cancelling the pending delay. Takes the value
   * explicitly because callers usually flush in the same tick they set it, when
   * the hook's own `value` is still the previous render's.
   */
  flush: (next: T) => void
}

/**
 * Trails `value` by `delay`. Search fires a network request per settled value,
 * so the delay is what keeps a typed word from costing eight provider searches.
 */
export function useDebouncedValue<T>(value: T, delay = 300): DebouncedValue<T> {
  const [debounced, setDebounced] = useState(value)

  useEffect(() => {
    if (Object.is(value, debounced)) return
    const timer = window.setTimeout(() => setDebounced(value), delay)
    return () => window.clearTimeout(timer)
  }, [value, debounced, delay])

  return { debounced, flush: setDebounced }
}
